import { useState, useEffect, useRef } from "react";
import axios from "axios";
import canvasStorage from "../../../util/canvasStorage";
import { openCanvasDB, loadFromIndexedDB } from "../../../util/indexedDBUtils";
import { sanitizeId } from "../utils/textDataUtils";
import { normalizeShapeType, isMarkerId } from "../../../util/shapeTypeMapper";
import { BASE_URL } from "../../../config";
const TEXT_BOX_MATCH_TOLERANCE = 1;

const toNumber = (value) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getTextDimensions = (text = {}) => {
  const fallback = text.canvas_page_location_and_size || {};
  return {
    x: text.x ?? fallback.x ?? 0,
    y: text.y ?? fallback.y ?? 0,
    width: text.width ?? fallback.width ?? 0,
    height: text.height ?? fallback.height ?? 0,
    rotation: text.rotation ?? fallback.rotation ?? 0,
  };
};

const getTextBoxDimensions = (textBox = {}) => {
  return textBox.canvas_page_location_and_size || {};
};

const valuesAreClose = (a, b, tolerance = TEXT_BOX_MATCH_TOLERANCE) =>
  Math.abs(toNumber(a) - toNumber(b)) <= tolerance;

const textMatchesTextBox = (text, textBox) => {
  if (!text || !textBox) return false;

  const textDims = getTextDimensions(text);
  const textBoxDims = getTextBoxDimensions(textBox);

  return (
    valuesAreClose(textDims.x, textBoxDims.x) &&
    valuesAreClose(textDims.y, textBoxDims.y) &&
    valuesAreClose(textDims.width, textBoxDims.width) &&
    valuesAreClose(textDims.height, textBoxDims.height) &&
    valuesAreClose(textDims.rotation, textBoxDims.rotation || 0)
  );
};

const attachTextBoxMetadataToTexts = (texts = [], textBoxes = []) => {
  if (!Array.isArray(texts) || !Array.isArray(textBoxes) || textBoxes.length === 0) {
    return;
  }

  const remaining = [...textBoxes];

  texts.forEach((text) => {
    if (!text) {
      return;
    }

    const existingId =
      text.text_box_id ||
      text.textboxId ||
      text.textBoxId ||
      text.canvas_text_box_id ||
      text.canvasTextBoxId ||
      text.shape_info?.text_box_id ||
      text.shape_properties?.text_box_id;

    let matchIndex = -1;

    if (existingId) {
      matchIndex = remaining.findIndex(
        (tb) => tb?.text_box_id && tb.text_box_id === existingId
      );
    }

    if (matchIndex === -1) {
      matchIndex = remaining.findIndex((tb) => textMatchesTextBox(text, tb));
    }

    if (matchIndex === -1) {
      return;
    }

    const matched = remaining[matchIndex];
    text.text_box_id = text.text_box_id || matched.text_box_id;
    text.text_box_content_data = (matched.text_box_content || []).map(
      (entry) => ({
        ...entry,
        text_box_id: matched.text_box_id,
      })
    );
    if (matched.text_box_properties && !text.text_box_properties) {
      text.text_box_properties = matched.text_box_properties;
    }
    if (matched.canvas_page_location_and_size) {
      // Never overwrite the shape's position with the text_box's - texts with same text_box can have different x,y from API shapes
      const existing = text.canvas_page_location_and_size || {};
      const fromShape = {
        x: text.x ?? existing.x,
        y: text.y ?? existing.y,
        width: text.width ?? existing.width,
        height: text.height ?? existing.height,
        rotation: text.rotation ?? existing.rotation,
      };
      const fromBox = matched.canvas_page_location_and_size;
      text.canvas_page_location_and_size = {
        x: fromShape.x ?? fromBox.x,
        y: fromShape.y ?? fromBox.y,
        width: fromShape.width ?? fromBox.width,
        height: fromShape.height ?? fromBox.height,
        rotation: fromShape.rotation ?? fromBox.rotation ?? 0,
      };
    }

    remaining.splice(matchIndex, 1);
  });
};

const flattenTextBoxContent = (textBoxes = []) => {
  if (!Array.isArray(textBoxes) || textBoxes.length === 0) {
    return [];
  }

  const entries = [];
  textBoxes.forEach((textBox) => {
    if (!textBox) {
      return;
    }
    const contentArray = textBox.text_box_content;
    if (!Array.isArray(contentArray) || contentArray.length === 0) {
      return;
    }
    contentArray.forEach((content) => {
      entries.push({
        ...content,
        text_box_id: textBox.text_box_id,
      });
    });
  });

  return entries;
};

const collectPartDetailsFromCtmData = (sourceData) => {
  if (!sourceData || typeof sourceData !== "object") return [];
  if (Array.isArray(sourceData.part_details)) return sourceData.part_details;
  const ctmData = Array.isArray(sourceData.ctm_data) ? sourceData.ctm_data : [];
  return ctmData.flatMap((item) =>
    Array.isArray(item?.part_details) ? item.part_details : []
  );
};

const applyPartDetailsToPartsByType = (partsByType, partDetailsMap) => {
  if (!partsByType || typeof partsByType !== "object" || partDetailsMap.size === 0) {
    return partsByType;
  }

  const updated = {};
  Object.entries(partsByType).forEach(([typeName, group]) => {
    if (!group || !Array.isArray(group.parts)) {
      updated[typeName] = group;
      return;
    }

    updated[typeName] = {
      ...group,
      parts: group.parts.map((partItem) => {
        const matched = partDetailsMap.get(partItem?.part_ref);
        if (!matched) return partItem;
        let newPart = "";
        if (matched.part_number) newPart += `(${matched.part_number}) `;
        if (matched.description) newPart += matched.description;
        return {
          ...partItem,
          part: newPart || partItem.part,
          textbox_note: matched.textbox_note || partItem.textbox_note,
          itemCodeNote: matched.textbox_note || partItem.itemCodeNote,
          type_name: matched.type_name || partItem.type_name,
        };
      }),
    };
  });
  return updated;
};

const applyPartDetailsToTexts = (texts = [], partDetailsMap = new Map()) => {
  if (!Array.isArray(texts) || texts.length === 0 || partDetailsMap.size === 0) {
    return texts;
  }
  return texts.map((text) => {
    const updatedPartsByType = applyPartDetailsToPartsByType(
      text.partsByType,
      partDetailsMap
    );
    const updatedTextBoxContent = Array.isArray(text.text_box_content_data)
      ? text.text_box_content_data.map((entry) => {
          const matched = partDetailsMap.get(entry?.part_ref);
          if (!matched) return entry;
          let newPart = "";
          if (matched.part_number) newPart += `(${matched.part_number}) `;
          if (matched.description) newPart += matched.description;
          return {
            ...entry,
            part: newPart || entry.part,
            textbox_note: matched.textbox_note || entry.textbox_note,
            itemCodeNote: matched.textbox_note || entry.itemCodeNote,
            type_name: matched.type_name || entry.type_name,
          };
        })
      : text.text_box_content_data;

    return {
      ...text,
      partsByType: updatedPartsByType,
      text_box_content_data: updatedTextBoxContent,
    };
  });
};

/**
 * useCanvasPersistence Hook
 *
 * Manages canvas form persistence and draft saving
 * Handles both localStorage (add mode) and IndexedDB (edit mode)
 *
 * @param {Object} params
 * @param {string} params.mode - "add" or "edit"
 * @param {string} params.id - Canvas ID (for edit mode)
 * @param {Object} params.canvasData - Fetched canvas data
 *
 * @returns {Object} Form state and persistence methods
 */
const useCanvasPersistence = ({ mode, id, canvasData, initialHasUnsavedChanges = false }) => {

  // CRITICAL: Use global tracking to prevent multiple instances from interfering
  const instanceKey = `canvasPersistence_${id}`;
  const hasActiveInstance = window.canvasPersistenceInstances?.[instanceKey] === true;

  // Mark this instance as active
  if (!window.canvasPersistenceInstances) {
    window.canvasPersistenceInstances = {};
  }
  window.canvasPersistenceInstances[instanceKey] = true;

  // CRITICAL: Use a ref to preserve the initialHasUnsavedChanges value across re-renders
  // This prevents the flag from being lost when the hook re-renders with different props
  const initialHasUnsavedChangesRef = useRef(initialHasUnsavedChanges);

  // Update the ref only if initialHasUnsavedChanges is true and we haven't set it yet
  // This ensures that once we detect changes, we don't lose that flag
  if (initialHasUnsavedChanges && !initialHasUnsavedChangesRef.current) {
    initialHasUnsavedChangesRef.current = true;
  }

  // Form state
  const [formData, setFormData] = useState({
    title: "",
    description: "",
  });

  const [canvasPages, setCanvasPages] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(initialHasUnsavedChanges);
  const [dataLoaded, setDataLoaded] = useState(false);

  // CRITICAL: Use a ref to track if hasUnsavedChanges has been set to true
  // This prevents the state from being reset by subsequent re-renders
  const hasUnsavedChangesRef = useRef(hasUnsavedChanges);

  // Update the ref whenever hasUnsavedChanges changes
  useEffect(() => {
    hasUnsavedChangesRef.current = hasUnsavedChanges;
  }, [hasUnsavedChanges]);

  // CRITICAL: Update hasUnsavedChanges when initialHasUnsavedChanges changes
  // This fixes the issue where the flag gets reset on subsequent hook calls
  useEffect(() => {
    // DEBUG: Check for debug flag to force unsaved changes
    const debugForceUnsaved = localStorage.getItem("debugForceUnsaved") === "true";

    // Use the ref value if it's true, otherwise use the prop value
    const effectiveInitialValue = initialHasUnsavedChangesRef.current || initialHasUnsavedChanges || debugForceUnsaved;
    if (effectiveInitialValue !== undefined) {
      // CRITICAL: Only update if we're not currently in an unsaved state
      // This prevents resetting from true to false due to re-renders
      setHasUnsavedChanges((currentValue) => {
        if (currentValue === true || hasUnsavedChangesRef.current === true) {
          return true; // Keep true if already true
        }
        const debugMsg = debugForceUnsaved ? " (DEBUG FORCED)" : "";

        // CRITICAL: Clear the fromMyCanvas flag after we've processed it
        // This prevents it from being processed again on remounts
        if (initialHasUnsavedChanges && !debugForceUnsaved) {
          localStorage.removeItem("fromMyCanvas");
        }

        return effectiveInitialValue;
      });
    }
  }, [initialHasUnsavedChanges]);

  // Initialize form data based on mode
  useEffect(() => {

    if (mode === "add") {
      // Add mode: Try to restore from localStorage
      try {
        const savedTitle = localStorage.getItem("canvas_title");
        const savedDescription = localStorage.getItem("canvas_description");
        // Check both canvas_pages and canvasPages for compatibility
        const savedPages =
          localStorage.getItem("canvas_pages") ||
          localStorage.getItem("canvasPages");

        if (savedTitle && savedTitle !== "undefined") {
          setFormData((prev) => ({ ...prev, title: savedTitle }));
        }

        if (savedDescription && savedDescription !== "undefined") {
          setFormData((prev) => ({ ...prev, description: savedDescription }));
        }

        if (savedPages && savedPages !== "undefined") {
          const parsed = JSON.parse(savedPages);
          const restoredPages = Array.isArray(parsed) ? parsed : [];

          if (restoredPages.length > 0) {
            setCanvasPages(restoredPages);
            setHasUnsavedChanges(true);
          }

          // Clear after successful restore
          localStorage.removeItem("canvas_pages");
          localStorage.removeItem("canvasPages");
        }
      } catch (err) {
        console.error("Error restoring add-mode canvas draft:", err);
      }
    } else if (mode === "edit") {
      // CRITICAL: Skip API fetch if we're returning from MyCanvas
      // This prevents overwriting good IndexedDB data with potentially bad API data
      if (window.isProcessingCanvasReturn?.current) {
        return;
      }

      // CRITICAL FIX: Check BOTH flags before proceeding with API fetch
      // preventApiCall is set by canvasLeaveHandler.js when leaving the canvas
      // fromMyCanvas is also set to indicate we're returning from canvas
      const fromMyCanvas = localStorage.getItem("fromMyCanvas") === "true";
      const preventApiCall = localStorage.getItem("preventApiCall") === "true";

      if (fromMyCanvas || preventApiCall) {

        // Load pages from localStorage and IndexedDB asynchronously
        const loadPagesFromStorage = async () => {
          let restoredPagesFromStorage = [];
          try {
            // Try to get pages from "canvasPages" first, then fall back to "originalCanvasPages"
            let savedPagesRaw = localStorage.getItem("canvasPages");
            if (!savedPagesRaw || savedPagesRaw === "undefined") {
              savedPagesRaw = localStorage.getItem("originalCanvasPages");
            }

            if (savedPagesRaw && savedPagesRaw !== "undefined") {
              const savedPages = JSON.parse(savedPagesRaw);
              if (Array.isArray(savedPages) && savedPages.length > 0) {
                restoredPagesFromStorage = savedPages;


                // Load elements from IndexedDB for each page (use same key as saveCanvasElementsToIndexedDB)
                const pagesWithElements = await Promise.all(
                  savedPages.map(async (page) => {
                    try {
                      const loadKey = sanitizeId(page.id, page.id);
                      let elementsResult = await loadFromIndexedDB(
                        "canvasElements",
                        loadKey
                      );
                      if (!elementsResult?.data && loadKey !== page.id) {
                        elementsResult = await loadFromIndexedDB("canvasElements", page.id);
                      }

                      if (elementsResult && elementsResult.data) {
                        const data = elementsResult.data;
                        const userTexts = (data.texts || []).filter((t) => t.createdOnCanvas === true);
                        return {
                          ...page,
                          elements: data,
                        };
                      }
                      return page;
                    } catch (error) {
                      return page;
                    }
                  })
                );

                // CRITICAL: Always set canvasPages from IndexedDB when returning from MyCanvas.
                // Do NOT guard with canvasPages.length === 0 - otherwise if canvasPages was
                // already populated (e.g. by another effect or stale state), we would never
                // apply the latest IndexedDB data and text note/task would appear lost.
                setCanvasPages(pagesWithElements);
              }
            }
          } catch (err) {
            console.error("Error reading pages from localStorage:", err);
          }

          // Clear flags after processing
          localStorage.removeItem('preventApiCall');
          localStorage.removeItem('fromMyCanvas');
          setDataLoaded(true);
        };

        loadPagesFromStorage();
        return;
      }

      if (!id) {
        return;
      }

      // CRITICAL: First, synchronously restore pages from localStorage if they exist
      // This must happen BEFORE API fetch to preserve newly added pages
      // Check localStorage for saved pages (from handleNavigateToCanvas or after adding page)
      let restoredPagesFromStorage = [];
      try {
        // Try to get pages from "canvasPages" first, then fall back to "originalCanvasPages"
        let savedPagesRaw = localStorage.getItem("canvasPages");
        if (!savedPagesRaw || savedPagesRaw === "undefined") {
          savedPagesRaw = localStorage.getItem("originalCanvasPages");
        }

        if (savedPagesRaw && savedPagesRaw !== "undefined") {
          const savedPages = JSON.parse(savedPagesRaw);
          if (Array.isArray(savedPages) && savedPages.length > 0) {
            restoredPagesFromStorage = savedPages;

            // CRITICAL: Immediately restore to state if canvasPages is empty
            // Do this synchronously before any async operations
            if (canvasPages.length === 0) {
              setCanvasPages(savedPages);
            }
          }
        }
      } catch (err) {
        console.error("Error reading pages from localStorage:", err);
      }

      // Edit mode: Fetch canvas pages from API and load elements to IndexedDB
      const fetchEditModeData = async () => {
        try {
          const partDetailsSource =
            canvasData?.detail?.current_canvas_data_json || canvasData?.detail;
          const partDetails = collectPartDetailsFromCtmData(partDetailsSource);
          const partDetailsMap = new Map();
          partDetails.forEach((part) => {
            if (part?.part_ref) partDetailsMap.set(part.part_ref, part);
          });

          // CRITICAL: If we already have pages with newly added ones, don't overwrite them
          // Only fetch from API on initial load (when dataLoaded is false and we have no pages)
          // This prevents losing newly added pages when navigating
          const currentPages =
            canvasPages.length > 0 ? canvasPages : restoredPagesFromStorage;
          const hasPages = currentPages.length > 0;
          const shouldSkipFetch = dataLoaded && hasPages;

          // CRITICAL: Check if we have stored initial API pages
          // If not, we MUST fetch them at least once to store the baseline
          const storageKey = `initialApiPages_${id}`;
          const hasStoredApiPages = !!localStorage.getItem(storageKey);


          // Skip fetch ONLY if:
          // 1. We already have pages loaded AND
          // 2. We already have stored the initial API pages baseline
          if (shouldSkipFetch && hasStoredApiPages) {
            return;
          }

          if (shouldSkipFetch && !hasStoredApiPages) {
          }

          // Fetch canvas data from API to get pages
          const response = await axios.get(
            `${BASE_URL}canvas-tree/get-by-id/${id}`
          );
          const data = response.data.results;

          // Set short description if available
          if (data.short_description) {
            localStorage.setItem("short_description", data.short_description);
          }

          // Process pages if they exist
          if (
            data.pages &&
            Array.isArray(data.pages) &&
            data.pages.length > 0
          ) {
            // Sort API pages by created_at ascending (earliest first)
            const sortedApiPages = [...data.pages].sort((a, b) => {
              const dateA = new Date(a?.created_at || 0);
              const dateB = new Date(b?.created_at || 0);
              return dateA - dateB;
            });

            // Process each page and load elements into IndexedDB
            const processedPages = await Promise.all(
              sortedApiPages.map(async (apiPage, pageIndex) => {
                const pageId = apiPage.canvas_page_id || `page_${pageIndex}`;

                // Convert API shapes to MyCanvas format
                const convertApiShapesToMyCanvasFormat = (shapes) => {
                  const myCanvasFormat = {
                    texts: [],
                    rectangles: [],
                    circles: [],
                    arrows: [],
                    lines: [],
                    doubleSidedArrows: [],
                    images: [],
                    scribbles: [],
                  };

                  if (!shapes || !Array.isArray(shapes)) {
                    return myCanvasFormat;
                  }
                  const isValidPoints = (arr) =>
                    Array.isArray(arr) && arr.length >= 4;
                  const isLikelyDefaultPoints = (arr) => {
                    if (!isValidPoints(arr) || arr.length !== 4) return false;
                    const [x1, y1, x2, y2] = arr.map((v) => Number(v) || 0);
                    return (
                      (Math.abs(x1) < 0.001 &&
                        Math.abs(y1) < 0.001 &&
                        Math.abs(x2 - 100) < 0.001 &&
                        Math.abs(y2) < 0.001) ||
                      (Math.abs(x1) < 0.001 &&
                        Math.abs(y1) < 0.001 &&
                        Math.abs(x2 - 100) < 0.001 &&
                        Math.abs(y2 - 100) < 0.001)
                    );
                  };
                  const isDegeneratePoints = (arr) => {
                    if (!isValidPoints(arr)) return true;
                    let totalLength = 0;
                    for (let i = 0; i < arr.length - 2; i += 2) {
                      const x1 = Number(arr[i]) || 0;
                      const y1 = Number(arr[i + 1]) || 0;
                      const x2 = Number(arr[i + 2]) || 0;
                      const y2 = Number(arr[i + 3]) || 0;
                      totalLength += Math.hypot(x2 - x1, y2 - y1);
                    }
                    return totalLength < 0.5;
                  };
                  const resolveIncomingLinePoints = (shape, shapeData) => {
                    const rootPts = isValidPoints(shapeData?.points)
                      ? shapeData.points
                      : null;
                    const propsPts = isValidPoints(shape?.shape_properties?.points)
                      ? shape.shape_properties.points
                      : null;
                    const infoPts = isValidPoints(shape?.shape_info?.points)
                      ? shape.shape_info.points
                      : null;
                    if (
                      rootPts &&
                      !isLikelyDefaultPoints(rootPts) &&
                      !isDegeneratePoints(rootPts)
                    ) {
                      return rootPts;
                    }
                    if (propsPts && !isDegeneratePoints(propsPts)) {
                      return propsPts;
                    }
                    if (infoPts && !isDegeneratePoints(infoPts)) {
                      return infoPts;
                    }
                    if (rootPts) return rootPts;
                    if (propsPts) return propsPts;
                    if (infoPts) return infoPts;
                    return [0, 0, 100, 100];
                  };
                  const debugLinePointResolution = (
                    tag,
                    shape,
                    shapeData,
                    chosenPoints,
                    relativePoints,
                  ) => {
                    const sid =
                      shape?.shape_id ||
                      shapeData?.shape_id ||
                      shapeData?.id ||
                      shape?.id;
                    const rootPts = shapeData?.points;
                    const propsPts = shape?.shape_properties?.points;
                    const infoPts = shape?.shape_info?.points;
                    const summarize = (arr) =>
                      Array.isArray(arr)
                        ? {
                          len: arr.length,
                          first: arr.slice(0, 6),
                          last: arr.slice(Math.max(0, arr.length - 6)),
                        }
                        : null;
                    console.log("[CanvasPersistenceDebug][line-points]", {
                      tag,
                      shapeId: sid,
                      type: normalizeShapeType(shape?.type),
                      x: shapeData?.x,
                      y: shapeData?.y,
                      root: summarize(rootPts),
                      shape_properties: summarize(propsPts),
                      shape_info: summarize(infoPts),
                      chosen: summarize(chosenPoints),
                      relative: summarize(relativePoints),
                    });
                  };

                  shapes.forEach((shape) => {
                    const shapeProperties = shape.shape_properties || {};
                    const shapeInfo = shape.shape_info || {};
                    // Merge both payload branches so text metadata (e.g. partsByType)
                    // is not dropped when API sends it only in shape_info.
                    const shapeData = {
                      ...shapeInfo,
                      ...Object.fromEntries(
                        Object.entries(shapeProperties).filter(
                          ([, value]) => value !== null && value !== undefined,
                        ),
                      ),
                    };
                    const normalizedType = normalizeShapeType(shape.type);

                    switch (normalizedType) {
                      case "Text":
                        // CRITICAL: Skip shapes that only have a marker id (layout_config / parts) - API has 2 texts, never create 3rd from marker
                        if (isMarkerId(shape.shape_id) || (!shape.shape_id && shapeData.id && isMarkerId(shapeData.id))) {
                          break;
                        }
                        // CRITICAL: Prioritize shape_id from API over id from shape_properties
                        const textId =
                          shape.shape_id ||
                          (shapeData.id && !isMarkerId(shapeData.id) ? shapeData.id : null) ||
                          `text_${Date.now()}_${Math.random()}`;
                        // Default strokeWidth to 1 if not present in API response for text elements
                        const strokeWidth = shapeData.strokeWidth !== undefined && shapeData.strokeWidth !== null
                          ? shapeData.strokeWidth
                          : (shapeData.stroke_size !== undefined && shapeData.stroke_size !== null
                            ? shapeData.stroke_size
                            : 1);
                        // CRITICAL: Position only from this shape; do not spread shapeData so another shape's x,y never overwrite.
                        const shapeLocation = shape.canvas_page_location_and_size || shapeData.canvas_page_location_and_size;
                        const x = shapeData.x ?? shapeLocation?.x ?? 0;
                        const y = shapeData.y ?? shapeLocation?.y ?? 0;
                        const width = shapeData.width ?? shapeLocation?.width ?? 200;
                        const height = shapeData.height ?? shapeLocation?.height ?? 100;
                        const { shape_info: nestedShapeInfo, ...shapeDataFlat } = shapeData;
                        const partsByTypeFromShape =
                          shape.partsByType ??
                          shapeProperties.partsByType ??
                          shapeInfo.partsByType ??
                          shapeData.partsByType ??
                          nestedShapeInfo?.partsByType;
                        const textObj = {
                          id: textId,
                          shape_id: shape.shape_id,
                          x,
                          y,
                          width,
                          height,
                          partsByType: partsByTypeFromShape,
                          content: shapeData.content,
                          task: shapeData.task,
                          note: shapeData.note,
                          showBackground: shapeData.showBackground !== false,
                          name: shapeData.name || "Text Box",
                          strokeWidth,
                          ...(shapeLocation && { canvas_page_location_and_size: { ...shapeLocation, x, y, width, height } }),
                        };
                        Object.keys(shapeDataFlat).forEach((k) => {
                          if (!["id", "x", "y", "width", "height", "shape_id", "canvas_page_location_and_size"].includes(k)) {
                            textObj[k] = shapeDataFlat[k];
                          }
                        });
                        myCanvasFormat.texts.push(textObj);
                        break;
                      case "Circle":
                        // CRITICAL: Prioritize shape_id from API over id from shape_properties
                        // shape_id is the authoritative ID from the database
                        // shape_properties.id may contain marker IDs that should be ignored
                        const circleId =
                          shape.shape_id ||
                          (shapeData.id && !isMarkerId(shapeData.id) ? shapeData.id : null) ||
                          `circle_${Date.now()}_${Math.random()}`;
                        myCanvasFormat.circles.push({
                          x: shapeData.x || 0,
                          y: shapeData.y || 0,
                          radius: shapeData.radius || 50,
                          fill:
                            shapeData.fill || shapeData.fillColor || "#ffffff",
                          stroke: shapeData.stroke || "#000000",
                          strokeWidth: shapeData.strokeWidth || 2,
                          name: shapeData.name || "Circle",
                          ...shapeData,
                          id: circleId,
                        });
                        break;
                      case "Rect":
                        // CRITICAL: Prioritize shape_id from API over id from shape_properties
                        // shape_id is the authoritative ID from the database
                        // shape_properties.id may contain marker IDs that should be ignored
                        const rectId =
                          shape.shape_id ||
                          (shapeData.id && !isMarkerId(shapeData.id) ? shapeData.id : null) ||
                          `rect_${Date.now()}_${Math.random()}`;
                        myCanvasFormat.rectangles.push({
                          x: shapeData.x || 0,
                          y: shapeData.y || 0,
                          width: shapeData.width || 100,
                          height: shapeData.height || 100,
                          fill:
                            shapeData.fill || shapeData.fillColor || "#ffffff",
                          stroke: shapeData.stroke || "#000000",
                          strokeWidth: shapeData.strokeWidth || 2,
                          name: shapeData.name || "Rectangle",
                          ...shapeData,
                          id: rectId,
                        });
                        break;
                      case "Arrow": {
                        const arrowX = Number.isFinite(shapeData.x)
                          ? shapeData.x
                          : 0;
                        const arrowY = Number.isFinite(shapeData.y)
                          ? shapeData.y
                          : 0;
                        const incoming = resolveIncomingLinePoints(
                          shape,
                          shapeData,
                        );

                        // Treat points as absolute only when they are anchored at element x/y.
                        // Relative points may start at non-zero local offsets (e.g. [88,0,0,11]).
                        const firstPointX = incoming[0] || 0;
                        const firstPointY = incoming[1] || 0;
                        const isAbsoluteLike =
                          Math.abs(firstPointX - arrowX) < 0.1 &&
                          Math.abs(firstPointY - arrowY) < 0.1;

                        const relativePoints = [];
                        if (!isAbsoluteLike) {
                          // Points are already relative, use them as-is
                          relativePoints.push(...incoming);
                        } else {
                          // Points are absolute, convert to relative
                          for (let i = 0; i < incoming.length; i += 2) {
                            relativePoints.push(
                              (incoming[i] || 0) - arrowX,
                              (incoming[i + 1] || 0) - arrowY
                            );
                          }
                        }
                        debugLinePointResolution(
                          "Arrow",
                          shape,
                          shapeData,
                          incoming,
                          relativePoints,
                        );
                        // CRITICAL: Prioritize shape_id from API over id from shape_properties
                        // shape_id is the authoritative ID from the database
                        // shape_properties.id may contain marker IDs that should be ignored
                        const arrowId =
                          shape.shape_id ||
                          (shapeData.id && !isMarkerId(shapeData.id) ? shapeData.id : null) ||
                          `arrow_${Date.now()}_${Math.random()}`;
                        // CRITICAL: For Arrow types, set arrow head properties
                        // hasEndArrow should default to true for Arrow type
                        const hasStartArrow = shapeData.hasStartArrow !== undefined
                          ? shapeData.hasStartArrow
                          : shapeData.pointerAtBeginning || false;
                        // For Arrow types, hasEndArrow should default to true unless explicitly false
                        const hasEndArrow = shapeData.pointerAtEnd === false
                          ? false
                          : (shapeData.hasEndArrow !== undefined
                            ? shapeData.hasEndArrow
                            : true); // Default to true for Arrow type
                        myCanvasFormat.arrows.push({
                          ...shapeData,
                          x: arrowX,
                          y: arrowY,
                          points: relativePoints,
                          stroke:
                            shapeData.stroke ||
                            shapeData.strokeColor ||
                            "#000000",
                          strokeWidth: shapeData.strokeWidth || 2,
                          fill:
                            shapeData.fill || shapeData.fillColor || "#000000",
                          name: shapeData.name || "Arrow",
                          strokeColor:
                            shapeData.stroke ||
                            shapeData.strokeColor ||
                            "#000000",
                          hasStartArrow: Boolean(hasStartArrow),
                          hasEndArrow: Boolean(hasEndArrow),
                          type: "Arrow",
                          id: arrowId,
                          shape_id: shape.shape_id,
                        });
                        break;
                      }
                      case "Line": {
                        // CRITICAL: API may return top-level type "Line" but shape_properties.type "Arrow" with hasEndArrow: true.
                        // Use shape_properties.type when present so we route to the correct array for edit-mode canvas.
                        const effectiveLineType = normalizeShapeType(shapeData.type) || normalizedType;

                        const lineX = shapeData.x || 0;
                        const lineY = shapeData.y || 0;
                        const absoluteLinePoints = resolveIncomingLinePoints(
                          shape,
                          shapeData,
                        );

                        // Treat points as absolute only when they are anchored at element x/y.
                        // Relative points can legitimately start away from (0,0).
                        const firstPointX = absoluteLinePoints[0] || 0;
                        const firstPointY = absoluteLinePoints[1] || 0;
                        const isAbsoluteLike =
                          Math.abs(firstPointX - lineX) < 0.1 &&
                          Math.abs(firstPointY - lineY) < 0.1;

                        const relativeLinePoints = [];
                        if (!isAbsoluteLike) {
                          // Points are already relative, use them as-is
                          relativeLinePoints.push(...absoluteLinePoints);
                        } else {
                          // Points are absolute, convert to relative
                          for (let i = 0; i < absoluteLinePoints.length; i += 2) {
                            const absX = absoluteLinePoints[i];
                            const absY = absoluteLinePoints[i + 1];
                            relativeLinePoints.push(
                              (absX || 0) - lineX,
                              (absY || 0) - lineY
                            );
                          }
                        }
                        debugLinePointResolution(
                          `Line->${effectiveLineType}`,
                          shape,
                          shapeData,
                          absoluteLinePoints,
                          relativeLinePoints,
                        );
                        // CRITICAL: Prioritize shape_id from API over id from shape_properties
                        const lineId =
                          shape.shape_id ||
                          (shapeData.id && !isMarkerId(shapeData.id) ? shapeData.id : null) ||
                          `line_${Date.now()}_${Math.random()}`;

                        if (effectiveLineType === "Arrow") {
                          const hasStartArrow = shapeData.hasStartArrow !== undefined
                            ? shapeData.hasStartArrow
                            : shapeData.pointerAtBeginning || false;
                          const hasEndArrow = shapeData.pointerAtEnd === false
                            ? false
                            : (shapeData.hasEndArrow !== undefined ? shapeData.hasEndArrow : true);
                          myCanvasFormat.arrows.push({
                            ...shapeData,
                            x: lineX,
                            y: lineY,
                            points: relativeLinePoints,
                            stroke: shapeData.stroke || shapeData.strokeColor || "#000000",
                            strokeWidth: shapeData.strokeWidth || 2,
                            fill: shapeData.fill || shapeData.fillColor || "#000000",
                            name: shapeData.name || "Arrow",
                            strokeColor: shapeData.stroke || shapeData.strokeColor || "#000000",
                            hasStartArrow: Boolean(hasStartArrow),
                            hasEndArrow: Boolean(hasEndArrow),
                            type: "Arrow",
                            startDot: shapeData.startDot,
                            endDot: shapeData.endDot,
                            id: lineId,
                            shape_id: shape.shape_id,
                          });
                        } else if (effectiveLineType === "DoubleSidedArrow") {
                          const hasStartArrow = shapeData.hasStartArrow !== undefined
                            ? shapeData.hasStartArrow
                            : shapeData.pointerAtBeginning !== undefined ? shapeData.pointerAtBeginning : true;
                          const hasEndArrow = shapeData.hasEndArrow !== undefined
                            ? shapeData.hasEndArrow
                            : shapeData.pointerAtEnd !== undefined ? shapeData.pointerAtEnd : true;
                          myCanvasFormat.doubleSidedArrows.push({
                            ...shapeData,
                            x: lineX,
                            y: lineY,
                            points: relativeLinePoints,
                            stroke: shapeData.stroke || shapeData.strokeColor || "#000000",
                            strokeWidth: shapeData.strokeWidth || 2,
                            fill: shapeData.fill || shapeData.fillColor || "#000000",
                            name: shapeData.name || "Double Sided Arrow",
                            strokeColor: shapeData.stroke || shapeData.strokeColor || "#000000",
                            hasStartArrow: Boolean(hasStartArrow),
                            hasEndArrow: Boolean(hasEndArrow),
                            type: "DoubleSidedArrow",
                            startDot: shapeData.startDot,
                            endDot: shapeData.endDot,
                            id: lineId,
                            shape_id: shape.shape_id,
                          });
                        } else {
                          myCanvasFormat.lines.push({
                            x: lineX,
                            y: lineY,
                            points: relativeLinePoints,
                            stroke:
                              shapeData.stroke ||
                              shapeData.strokeColor ||
                              "#000000",
                            strokeWidth: shapeData.strokeWidth || 2,
                            startDot: shapeData.startDot,
                            endDot: shapeData.endDot,
                            name: shapeData.name || "Line",
                            ...shapeData,
                            id: lineId,
                            shape_id: shape.shape_id,
                          });
                        }
                        break;
                      }
                      case "DoubleSidedArrow": {
                        const doubleArrowX = Number.isFinite(shapeData.x)
                          ? shapeData.x
                          : 0;
                        const doubleArrowY = Number.isFinite(shapeData.y)
                          ? shapeData.y
                          : 0;
                        const incoming = resolveIncomingLinePoints(
                          shape,
                          shapeData,
                        );

                        // Treat points as absolute only when they are anchored at element x/y.
                        const firstPointX = incoming[0] || 0;
                        const firstPointY = incoming[1] || 0;
                        const isAbsoluteLike =
                          Math.abs(firstPointX - doubleArrowX) < 0.1 &&
                          Math.abs(firstPointY - doubleArrowY) < 0.1;

                        const relativePoints = [];
                        if (!isAbsoluteLike) {
                          // Points are already relative, use them as-is
                          relativePoints.push(...incoming);
                        } else {
                          // Points are absolute, convert to relative
                          for (let i = 0; i < incoming.length; i += 2) {
                            relativePoints.push(
                              (incoming[i] || 0) - doubleArrowX,
                              (incoming[i + 1] || 0) - doubleArrowY
                            );
                          }
                        }
                        debugLinePointResolution(
                          "DoubleSidedArrow",
                          shape,
                          shapeData,
                          incoming,
                          relativePoints,
                        );
                        // CRITICAL: Prioritize shape_id from API over id from shape_properties
                        // shape_id is the authoritative ID from the database
                        // shape_properties.id may contain marker IDs that should be ignored
                        const doubleArrowId =
                          shape.shape_id ||
                          (shapeData.id && !isMarkerId(shapeData.id) ? shapeData.id : null) ||
                          `double_arrow_${Date.now()}_${Math.random()}`;
                        // CRITICAL: For DoubleSidedArrow types, both arrow heads should be true
                        const hasStartArrow = shapeData.hasStartArrow !== undefined
                          ? shapeData.hasStartArrow
                          : shapeData.pointerAtBeginning !== undefined
                            ? shapeData.pointerAtBeginning
                            : true; // Default to true for DoubleSidedArrow
                        const hasEndArrow = shapeData.hasEndArrow !== undefined
                          ? shapeData.hasEndArrow
                          : shapeData.pointerAtEnd !== undefined
                            ? shapeData.pointerAtEnd
                            : true; // Default to true for DoubleSidedArrow
                        myCanvasFormat.doubleSidedArrows.push({
                          ...shapeData,
                          x: doubleArrowX,
                          y: doubleArrowY,
                          points: relativePoints,
                          stroke:
                            shapeData.stroke ||
                            shapeData.strokeColor ||
                            "#000000",
                          strokeWidth: shapeData.strokeWidth || 2,
                          fill:
                            shapeData.fill || shapeData.fillColor || "#000000",
                          name: shapeData.name || "Double Sided Arrow",
                          strokeColor:
                            shapeData.stroke ||
                            shapeData.strokeColor ||
                            "#000000",
                          hasStartArrow: Boolean(hasStartArrow),
                          hasEndArrow: Boolean(hasEndArrow),
                          type: "DoubleSidedArrow",
                          id: doubleArrowId,
                          shape_id: shape.shape_id,
                        });
                        break;
                      }
                    }
                  });

                  return myCanvasFormat;
                };

                // CRITICAL: Only use shapes with type "Text" for canvas texts - never add canvas text elements from image text_boxes.
                // Collect text_box_ids that belong to images so we exclude them from standalone canvas texts.
                const imageTextBoxIds = new Set();
                (apiPage.images || []).forEach((img) => {
                  (img.text_boxes || []).forEach((tb) => {
                    if (tb?.text_box_id) imageTextBoxIds.add(tb.text_box_id);
                  });
                });
                const shapesForConversion = (apiPage.shapes || []).filter(
                  (s) => {
                    const norm = normalizeShapeType(s?.type);
                    if (norm !== "Text") return true; // keep non-Text shapes for other arrays
                    const sid = s?.shape_id || s?.shape_properties?.shape_id;
                    return !sid || !imageTextBoxIds.has(sid);
                  }
                );

                // Build validTextShapeIds from API shapes (so we always have 2 when API has 2, regardless of conversion)
                const apiTextShapeIdsFromShapes = (apiPage.shapes || [])
                  .filter((s) => normalizeShapeType(s?.type) === "Text" && s?.shape_id && !isMarkerId(s.shape_id) && !imageTextBoxIds.has(s.shape_id))
                  .map((s) => s.shape_id)
                  .filter(Boolean);

                // Convert shapes (texts come only from shapes with type Text, never from text_boxes)
                const elements = convertApiShapesToMyCanvasFormat(
                  shapesForConversion
                );
                // Attach metadata only to existing texts; do not create new texts from text_boxes
                attachTextBoxMetadataToTexts(
                  elements.texts,
                  apiPage.text_boxes || []
                );
                if (partDetailsMap.size > 0) {
                  elements.texts = applyPartDetailsToTexts(
                    elements.texts || [],
                    partDetailsMap
                  );
                }

                // Process images if they exist
                if (apiPage.images && Array.isArray(apiPage.images)) {
                  apiPage.images.forEach((imageData) => {
                    if (imageData.image_path) {
                      const imgProps = imageData.image_properties || {};
                      const imageId =
                        imageData.id ||
                        imageData.image_id ||
                        `api_image_${Date.now()}_${Math.random()}`;

                      const existingImage = elements.images.find(
                        (img) => img.id === imageId
                      );
                      if (!existingImage) {
                        elements.images.push({
                          ...imgProps,
                          id: imageId,
                          shape_id: imageData.image_id,
                          image_id: imageId,
                          x: imgProps.x ?? 0,
                          y: imgProps.y ?? 0,
                          width: imgProps.width ?? 100,
                          height: imgProps.height ?? 100,
                          src: imageData.image_path,
                          image_path: imageData.image_path,
                          name: imageData.name || imgProps.name || "API Image",
                          rotation: imgProps.rotation ?? 0,
                          scaleX: imgProps.scaleX ?? 1,
                          scaleY: imgProps.scaleY ?? 1,
                          cropData: imgProps.cropData || imageData.cropData,
                          originalImageDataUrl:
                            imgProps.originalImageDataUrl ||
                            imageData.originalImageDataUrl,
                          imageDataUrl:
                            imgProps.imageDataUrl || imageData.imageDataUrl,
                          maintainAspectRatio: imgProps.maintainAspectRatio,
                          compress: imgProps.compress,
                          originalCropRotation: imgProps.originalCropRotation,
                          hasBakedTransforms: imgProps.hasBakedTransforms,
                          flipHorizontal: imgProps.flipHorizontal,
                          flipVertical: imgProps.flipVertical,
                          originalWidth: imgProps.originalWidth,
                          originalHeight: imgProps.originalHeight,
                          originalAspectRatio: imgProps.originalAspectRatio,
                          uncompressedCroppedImageDataUrl:
                            imgProps.uncompressedCroppedImageDataUrl ||
                            imgProps.uncompressedCroppedImageUrl,
                          referenceUrl:
                            imgProps.referenceUrl ||
                            imageData.referenceUrl ||
                            "",
                        });
                      }
                    }
                  });
                }

                // Save elements to IndexedDB
                // CRITICAL: Only overwrite IndexedDB if it doesn't already have newer data
                // This preserves user-created elements that haven't been saved to the server yet
                try {
                  const db = await openCanvasDB();
                  if (db.objectStoreNames.contains("canvasElements")) {
                    const transaction = db.transaction(
                      ["canvasElements"],
                      "readwrite"
                    );
                    const store = transaction.objectStore("canvasElements");

                    // Check if IndexedDB already has data for this page
                    const existingResult = await new Promise(
                      (resolve, reject) => {
                        const getRequest = store.get(pageId);
                        getRequest.onsuccess = () => resolve(getRequest.result);
                        getRequest.onerror = () => reject(getRequest.error);
                      }
                    );

                    let elementsToSave;

                    if (existingResult && existingResult.data) {
                      // IndexedDB-first policy: if a page row exists, it is authoritative
                      // (including intentionally empty arrays after deletes). Never repopulate
                      // deleted entities from API get-by-id in this path.
                      elementsToSave = {
                        ...existingResult.data,
                        snapshot:
                          existingResult.data.snapshot ||
                          apiPage.canva_snap_url ||
                          null,
                        canva_snap_url:
                          existingResult.data.canva_snap_url ||
                          apiPage.canva_snap_url ||
                          null,
                        canvasHeight:
                          existingResult.data.canvasHeight ||
                          apiPage.layout_config?.layout?.page_height_vh ||
                          100,
                        validTextShapeIds:
                          existingResult.data.validTextShapeIds ||
                          (apiTextShapeIdsFromShapes.length > 0
                            ? apiTextShapeIdsFromShapes
                            : undefined),
                      };
                    } else {
                      // No existing data, save API data
                      const apiValidTextShapeIds = apiTextShapeIdsFromShapes.length > 0 ? apiTextShapeIdsFromShapes : (elements.texts || []).map((t) => t.shape_id).filter(Boolean);
                      elementsToSave = {
                        ...elements,
                        snapshot: apiPage.canva_snap_url || null,
                        canva_snap_url: apiPage.canva_snap_url || null,
                        canvasHeight:
                          apiPage.layout_config?.layout?.page_height_vh || 100,
                        ...(apiValidTextShapeIds.length > 0 ? { validTextShapeIds: apiValidTextShapeIds } : {}),
                      };
                    }

                    // Put with proper structure: { id, data }
                    await store.put({
                      id: pageId,
                      data: elementsToSave,
                      lastModified:
                        existingResult?.lastModified ||
                        new Date().toISOString(),
                    });
                  }
                } catch (error) {
                  console.error(
                    `Error saving page ${pageIndex + 1} elements to IndexedDB:`,
                    error
                  );
                }

                // CRITICAL: Load elements from IndexedDB for this page
                // We've already saved/verified IndexedDB data above, now load it for the page object
                let pageElements = null;
                let pageSnapshot = apiPage.canva_snap_url || null;
                let pageCanvasHeight =
                  apiPage.layout_config?.layout?.page_height_vh || 100;

                try {
                  const db = await openCanvasDB();
                  if (db.objectStoreNames.contains("canvasElements")) {
                    const transaction = db.transaction(
                      ["canvasElements"],
                      "readonly"
                    );
                    const store = transaction.objectStore("canvasElements");
                    const elementsResult = await new Promise(
                      (resolve, reject) => {
                        const getRequest = store.get(pageId);
                        getRequest.onsuccess = () => resolve(getRequest.result);
                        getRequest.onerror = () => reject(getRequest.error);
                      }
                    );

                    if (elementsResult && elementsResult.data) {
                      pageElements = elementsResult.data;
                      attachTextBoxMetadataToTexts(
                        pageElements.texts,
                        apiPage.text_boxes || []
                      );
                      // Use IndexedDB snapshot if available, otherwise use API canva_snap_url
                      pageSnapshot =
                        elementsResult.data.snapshot ||
                        apiPage.canva_snap_url ||
                        null;
                      pageCanvasHeight =
                        elementsResult.data.canvasHeight ||
                        apiPage.layout_config?.layout?.page_height_vh ||
                        100;
                    }
                  }
                } catch (error) {
                }

                return {
                  id: pageId,
                  canvas_page_id: apiPage.canvas_page_id || pageId, // Preserve canvas_page_id from API for tracking removed pages
                  snapshot: pageSnapshot,
                  canva_snap_url: apiPage.canva_snap_url || null,
                  lastModified: apiPage.created_at || new Date().toISOString(),
                  templateName: "",
                  templateApplied: false,
                  zoomLevel: 100,
                  canvasHeight: pageCanvasHeight,
                  // API shapes so CanvasForm can merge 2 texts and pass validTextShapeIds even when processedCanvasPagesFromApi is null
                  shapes: apiPage.shapes || [],
                  images: apiPage.images || [],
                  text_boxes: apiPage.text_boxes || [],
                  text_box_content_data: flattenTextBoxContent(
                    apiPage.text_boxes || []
                  ),
                  elements: pageElements
                    ? {
                      texts: pageElements.texts || [],
                      rectangles: pageElements.rectangles || [],
                      circles: pageElements.circles || [],
                      arrows: pageElements.arrows || [],
                      lines: pageElements.lines || [],
                      doubleSidedArrows: pageElements.doubleSidedArrows || [],
                      scribbles: pageElements.scribbles || [],
                      images: pageElements.images || [],
                    }
                    : undefined, // Only set if we have elements from IndexedDB
                };
              })
            );

            // CRITICAL: Store the API baseline AFTER all IndexedDB writes are complete
            // This captures the pure API state for accurate change detection
            try {
              const storageKey = `initialApiPages_${id}`;

              // CRITICAL: Always store baseline when API data is loaded
              // ViewChildNode clears the baseline before navigating to CanvasForm
              // So if we're here, it means we need to capture a fresh baseline

              // Wait a bit to ensure all IndexedDB writes are complete
              // The processedPages Promise.all above should have completed all writes,
              // but we add a small delay to be safe
              await new Promise(resolve => setTimeout(resolve, 100));

              // Create baseline from IndexedDB (after API data has been saved)
              const baselinePages = await Promise.all(
                processedPages.map(async (page) => {
                  const pageId = page.canvas_page_id || page.id;

                  // Load the ORIGINAL API data from IndexedDB (just saved above)
                  try {
                    const db = await openCanvasDB();
                    if (db.objectStoreNames.contains("canvasElements")) {
                      const transaction = db.transaction(["canvasElements"], "readonly");
                      const store = transaction.objectStore("canvasElements");
                      const result = await new Promise((resolve, reject) => {
                        const getRequest = store.get(pageId);
                        getRequest.onsuccess = () => resolve(getRequest.result);
                        getRequest.onerror = () => reject(getRequest.error);
                      });

                      if (result && result.data) {
                        // CRITICAL: Convert arrows to unified format to match MyCanvas behavior
                        // MyCanvas converts arrows/doubleSidedArrows to lines, so we need to do the same for the baseline
                        const arrows = result.data.arrows || [];
                        const lines = result.data.lines || [];
                        const doubleSidedArrows = result.data.doubleSidedArrows || [];

                        // Merge arrows and doubleSidedArrows into lines (unified format)
                        const unifiedLines = [...lines, ...arrows, ...doubleSidedArrows];

                        return {
                          canvas_page_id: pageId,
                          canvasHeight: result.data.canvasHeight || 100,
                          images: result.data.images || [],
                          rectangles: result.data.rectangles || [],
                          circles: result.data.circles || [],
                          // CRITICAL: Store empty arrays for arrows/doubleSidedArrows since they're converted to lines
                          arrows: [],
                          lines: unifiedLines,
                          doubleSidedArrows: [],
                          texts: result.data.texts || [],
                          scribbles: result.data.scribbles || [],
                        };
                      }
                    }
                  } catch (error) {
                    console.error(`Error loading baseline for page ${pageId}:`, error);
                  }

                  return null;
                })
              );

              const validBaselinePages = baselinePages.filter(Boolean);

              if (validBaselinePages.length > 0) {
                localStorage.setItem(storageKey, JSON.stringify(validBaselinePages));

                // CRITICAL: Also log the actual image data to verify it's being captured
                validBaselinePages.forEach(p => {
                  if (p.images && p.images.length > 0) {
                  }
                });
              } else {
              }
            } catch (err) {
              console.error("Error storing API baseline:", err);
            }

            // CRITICAL: Merge with existing pages instead of replacing
            // This preserves newly added pages that aren't in the API yet
            setCanvasPages((existingPages) => {
              // Get pages from localStorage if existingPages is empty
              // Read from localStorage again inside the callback to ensure we have the latest
              let pagesFromStorage = [];
              try {
                const savedPagesRaw = localStorage.getItem("canvasPages");
                if (savedPagesRaw && savedPagesRaw !== "undefined") {
                  const savedPages = JSON.parse(savedPagesRaw);
                  if (Array.isArray(savedPages) && savedPages.length > 0) {
                    pagesFromStorage = savedPages;
                  }
                }
              } catch (err) {
                // Ignore errors
              }

              // CRITICAL: Filter pages from storage to only include pages that match the current canvas
              // This prevents loading pages from other canvases stored in IndexedDB/localStorage
              const apiPageIds = new Set(
                processedPages.map((p) => p.canvas_page_id || p.id)
              );
              const apiPageIdsById = new Set(processedPages.map((p) => p.id));

              // Filter pagesFromStorage to only include pages that match API response
              const filteredPagesFromStorage = pagesFromStorage.filter((page) => {
                if (!page || !page.id) return false;
                // Keep pages that match API pages by canvas_page_id or id
                return apiPageIds.has(page.canvas_page_id) ||
                  apiPageIdsById.has(page.id) ||
                  // Keep newly added pages (no canvas_page_id) that are in current session
                  (!page.canvas_page_id && existingPages.some(ep => ep.id === page.id));
              });

              // Filter restoredPagesFromStorage similarly
              const filteredRestoredPages = restoredPagesFromStorage.filter((page) => {
                if (!page || !page.id) return false;
                return apiPageIds.has(page.canvas_page_id) ||
                  apiPageIdsById.has(page.id) ||
                  (!page.canvas_page_id && existingPages.some(ep => ep.id === page.id));
              });

              // Log filtering results for debugging
              if (pagesFromStorage.length > filteredPagesFromStorage.length) {
              }
              if (restoredPagesFromStorage.length > filteredRestoredPages.length) {
              }

              const pagesToCheck =
                existingPages.length > 0
                  ? existingPages
                  : filteredPagesFromStorage.length > 0
                    ? filteredPagesFromStorage
                    : filteredRestoredPages;

              // If we have pages (from state or localStorage), merge them
              if (pagesToCheck && pagesToCheck.length > 0) {

                // Identify API page IDs - API pages have canvas_page_id, newly added pages don't
                // (already defined above for filtering)

                // Keep existing pages that aren't in the API (newly added pages)
                // CRITICAL: Newly added pages don't have canvas_page_id (it's undefined/null)
                // and their id doesn't match any API page id
                const newlyAddedPages = pagesToCheck.filter((existingPage) => {
                  if (!existingPage || !existingPage.id) {
                    return false;
                  }

                  // CRITICAL: If page has canvas_page_id, it came from API - not newly added
                  if (
                    existingPage.canvas_page_id !== undefined &&
                    existingPage.canvas_page_id !== null
                  ) {
                    return false; // This is an API page
                  }

                  // Check if this page's id exists in API pages by id
                  const isInApiById = apiPageIdsById.has(existingPage.id);

                  // Also check by canvas_page_id (should be false for newly added pages)
                  const isInApiByCanvasPageId = existingPage.canvas_page_id
                    ? apiPageIds.has(existingPage.canvas_page_id)
                    : false;

                  const isInApi = isInApiById || isInApiByCanvasPageId;

                  if (!isInApi) {
                  }

                  return !isInApi; // Keep if not in API
                });


                // Merge: API pages first, then newly added pages
                const mergedPages = [...processedPages, ...newlyAddedPages];



                // Ensure we didn't lose any pages
                // Compare with pagesToCheck (which includes localStorage pages) not just existingPages
                if (mergedPages.length < pagesToCheck.length) {
                  console.error(
                    `⚠️ Page count decreased after merge! Before: ${pagesToCheck.length}, After: ${mergedPages.length}`
                  );
                  console.error(
                    "Pages before merge:",
                    pagesToCheck.map((p) => ({
                      id: p.id,
                      canvas_page_id: p.canvas_page_id,
                      newlyAdded: !p.canvas_page_id,
                    }))
                  );
                  // Safety: preserve all pages - merge API pages with all existing pages
                  // Don't filter, just update API pages and keep existing ones
                  const apiPageIdsById = new Set(
                    processedPages.map((p) => p.id)
                  );
                  const apiPageIdsByCanvasPageId = new Set(
                    processedPages.map((p) => p.canvas_page_id).filter(Boolean)
                  );

                  // Update existing API pages with processed versions, keep newly added ones
                  const updatedPages = pagesToCheck.map((existingPage) => {
                    // If this is an API page (has canvas_page_id or id matches API), use processed version
                    if (
                      existingPage.canvas_page_id ||
                      apiPageIdsById.has(existingPage.id) ||
                      (existingPage.canvas_page_id &&
                        apiPageIdsByCanvasPageId.has(
                          existingPage.canvas_page_id
                        ))
                    ) {
                      // Find matching processed page
                      const matchingProcessedPage = processedPages.find(
                        (p) =>
                          p.canvas_page_id === existingPage.canvas_page_id ||
                          p.id === existingPage.id
                      );
                      return matchingProcessedPage || existingPage;
                    }
                    // Keep newly added pages as-is
                    return existingPage;
                  });

                  // Add any API pages that weren't in existing pages
                  const existingIds = new Set(
                    updatedPages.map((p) => p.id || p.canvas_page_id)
                  );
                  const newApiPages = processedPages.filter(
                    (p) =>
                      !existingIds.has(p.id) &&
                      !existingIds.has(p.canvas_page_id)
                  );

                  return [...updatedPages, ...newApiPages];
                }

                return mergedPages;
              }

              // No existing pages, use API pages
              return processedPages;
            });
          } else {
            // No pages in API - preserve existing pages if any
            setCanvasPages((existingPages) => {
              if (existingPages && existingPages.length > 0) {
                return existingPages;
              }
              return [];
            });
          }

          setDataLoaded(true);
        } catch (error) {
          console.error("Error fetching canvas data in edit mode:", error);
          setDataLoaded(true);
        }
      };

      // ADDITIONAL CHECK: Check if we have good data in IndexedDB before calling API
      // This prevents API calls that might overwrite good data with bad data
      const checkIndexedDBData = async () => {

        try {
          const db = await openCanvasDB();
          if (db.objectStoreNames.contains("canvasElements")) {
            // First, let's see what keys are available in the store
            const getAllKeys = () => {
              return new Promise((resolve, reject) => {
                const transaction = db.transaction(["canvasElements"], "readonly");
                const store = transaction.objectStore("canvasElements");
                const request = store.getAllKeys();

                request.onsuccess = () => {
                  resolve(request.result);
                };

                request.onerror = () => reject(request.error);
              });
            };

            const availableKeys = await getAllKeys();

            const transaction = db.transaction(["canvasElements"], "readonly");
            const store = transaction.objectStore("canvasElements");
            const request = store.get(id);

            request.onsuccess = () => {
              const result = request.result;

              if (result && result.data && result.data.lines) {
                const firstLine = result.data.lines[0];
                if (firstLine && (firstLine.points || firstLine.shape_info?.points)) {
                  setDataLoaded(true);
                  return;
                }
              }

              // If no good data found, proceed with API fetch
              fetchEditModeData();
            };

            request.onerror = () => {
              console.error("Error checking IndexedDB data, proceeding with API fetch");
              fetchEditModeData();
            };
          } else {
            fetchEditModeData();
          }
        } catch (error) {
          console.error("Error checking IndexedDB, proceeding with API fetch");
          fetchEditModeData();
        }
      };

      checkIndexedDBData();
    }
  }, [mode, id]);

  // Load elements from IndexedDB when returning from MyCanvas in edit mode
  useEffect(() => {
    const loadElementsFromIndexedDB = async () => {
      // Skip if we're currently processing a return from MyCanvas
      // This prevents overwriting pages that were just updated
      if (window.isProcessingCanvasReturn?.current) {
        return;
      }

      if (mode !== "edit" || !canvasPages || canvasPages.length === 0) return;

      // Try to load elements from IndexedDB for each page
      const updatedPages = await Promise.all(
        canvasPages.map(async (page) => {
          // Preserve newly added pages that might not have IndexedDB data yet
          if (!page || !page.id) {
            return page;
          }

          try {
            const elementsResult = await loadFromIndexedDB(
              "canvasElements",
              page.id
            );

            if (elementsResult && elementsResult.data) {
              const elements = elementsResult.data;

              // Prefer IndexedDB snapshot over API canva_snap_url
              // But preserve all other page properties, especially for newly added pages
              return {
                ...page, // Preserve all existing properties
                snapshot:
                  elements.snapshot || page.snapshot || page.canva_snap_url,
              };
            }

            // If no IndexedDB data, preserve the original page
            // This is important for newly added pages
            return page;
          } catch (error) {
            // Preserve original page on error, especially for newly added pages
            return page;
          }
        })
      );

      // Ensure we didn't lose any pages
      if (updatedPages.length !== canvasPages.length) {
        console.error(
          `⚠️ Page count mismatch! Expected ${canvasPages.length}, got ${updatedPages.length}`
        );
        // Preserve original pages if count doesn't match
        return;
      }

      // Only update if snapshots changed
      const snapshotsChanged = updatedPages.some(
        (page, idx) => page.snapshot !== canvasPages[idx]?.snapshot
      );

      if (snapshotsChanged) {
        setCanvasPages(updatedPages);
      }
    };

    loadElementsFromIndexedDB();
  }, [mode, canvasPages?.length]);

  // Auto-save for edit mode
  useEffect(() => {
    if (mode === "edit" && id && dataLoaded && hasUnsavedChanges) {
      const timeoutId = setTimeout(async () => {
        try {
          await canvasStorage.storeCanvas(
            id,
            canvasPages,
            formData.title,
            formData.description
          );
        } catch (err) {
          console.error("Auto-save (edit) to IndexedDB failed:", err);
        }
      }, 700);

      return () => clearTimeout(timeoutId);
    }
  }, [
    mode,
    id,
    dataLoaded,
    hasUnsavedChanges,
    canvasPages,
    formData.title,
    formData.description,
  ]);

  // Save for preview (both modes)
  const saveForPreview = async () => {
    if (mode === "add") {
      // Add mode: Save lightweight pages to localStorage (like CanvasOld)
      // Omit large elements to avoid localStorage bloat
      const lightweightPages = (Array.isArray(canvasPages) ? canvasPages : [])
        .map((p) => ({
          id: p?.id || p?.pageId,
          zoomLevel: typeof p?.zoomLevel === "number" ? p.zoomLevel : 100,
          lastModified: p?.lastModified || new Date().toISOString(),
          canvasHeight: p?.canvasHeight,
          newlyAdded: p?.newlyAdded,
          snapshot: p?.snapshot,
          shortDescription: p?.shortDescription,
          templateName: p?.templateName,
          canva_snap_url: p?.canva_snap_url,
          templateApplied: p?.templateApplied,
          text_boxes: Array.isArray(p?.text_boxes) ? p.text_boxes : [],
          text_box_content_data: Array.isArray(p?.text_box_content_data)
            ? p.text_box_content_data
            : [],
          // elements are intentionally omitted to avoid localStorage bloat
        }))
        .filter((p) => p.id);

      localStorage.setItem("canvasPages", JSON.stringify(lightweightPages));
    }
  };

  // CRITICAL: Clean up global instance tracking when component unmounts
  useEffect(() => {
    return () => {
      if (window.canvasPersistenceInstances?.[instanceKey]) {
        delete window.canvasPersistenceInstances[instanceKey];
      }
    };
  }, [instanceKey]);

  // Clear stored data
  const clearStoredData = async () => {
    try {
      if (mode === "add") {
        localStorage.removeItem("canvas_pages");
        localStorage.removeItem("canvas_title");
        localStorage.removeItem("canvas_description");
      } else {
        await canvasStorage.deleteCanvas(id);
        localStorage.removeItem("canvas_title");
        localStorage.removeItem("canvas_description");
      }
    } catch (error) {
      console.error("Error clearing stored data:", error);
    }
  };

  return {
    formData,
    setFormData,
    canvasPages,
    setCanvasPages,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    saveForPreview,
    clearStoredData,
  };
};

export default useCanvasPersistence;
